import { useEffect, useRef, useState } from 'react';

interface UseScriptOptions {
  /** Optional data-* attributes to set on the <script> element. */
  dataAttributes?: Record<string, string>;
}

// Load javascript for iFields / external SDKs
const useScript = (src: string, options: UseScriptOptions = {}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  // Store dataAttributes in a ref so the effect does not need it as a
  // dependency — data attributes are read once at mount time and are not
  // expected to update after the script element is created
  const dataAttributesRef = useRef(options.dataAttributes);
  dataAttributesRef.current = options.dataAttributes;

  useEffect(() => {
    let cancelled = false;

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      if (!cancelled) setIsLoaded(true);
    };
    script.onerror = () => {
      // eslint-disable-next-line no-console
      if (!cancelled)
        console.error(`useScript: failed to load script "${src}"`);
    };

    const attrs = dataAttributesRef.current;
    if (attrs) {
      Object.entries(attrs).forEach(([key, value]) => {
        script.setAttribute(`data-${key}`, value);
      });
    }

    document.body.appendChild(script);

    return () => {
      cancelled = true;
      document.body.removeChild(script);
    };
  }, [src]);

  return isLoaded;
};

export default useScript;
